import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PAGE = 12;

export const normalizeReel = (post) => {
  const firstMedia = (post.media || [])[0] || {};
  const firstAttach = (post.attachments || [])[0] || {};
  const url = firstMedia.url || firstMedia.secure_url || firstAttach.url || post.media_url || post.image_url;
  const isVideo = firstMedia.type === "video" || /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(url || "");
  const kind = isVideo ? "video" : url ? "image" : "text";
  return {
    ...post,
    kind,
    video_url: isVideo ? url : post.video_url || null,
    mp4_url: firstMedia.mp4_url || firstAttach.mp4_url || post.mp4_url || null,
    hls_url: firstMedia.hls_url || post.hls_url || null,
    duration: firstMedia.duration || post.duration || null,
    f_auto_url: firstMedia.f_auto_url || null,
    original_url: firstMedia.original_url || null,
    poster_url: firstMedia.poster_url || firstAttach.poster_url || post.poster_url || post.thumbnail_url || null,
    image_url: !isVideo ? url : post.image_url || post.thumbnail_url || null,
    thumbnail_url: post.thumbnail_url || firstMedia.poster_url || (!isVideo ? url : null),
    audio_name: post.audio?.name || post.audio_name || post.music?.title || null,
    sound_source_id: post.audio?.source_post_id || post.id,
    author: {
      id: post.user?.id || post.user_id || post.author?.id,
      name: post.user?.display_name || post.user?.name || post.author?.name,
      username: post.user?.username || post.user?.name || post.author?.name,
      avatar_url: post.user?.avatar_url || post.author?.avatar_url,
    },
    series: post.series || null,
  };
};

/**
 * useReelsFeed — infinite, algorithmic reels feed with dedupe + history.
 *  mode: "for_you" | "following" | { userId }  (profile mode = chronological)
 */
/* Caché en memoria del feed (por modo) para volver exactamente al mismo video al salir a un perfil/comentarios. */
const FEED_CACHE = new Map();
const CACHE_TTL = 15 * 60 * 1000;
export const rememberFeedPosition = (key, index) => { const c = FEED_CACHE.get(key); if (c) { c.index = index; c.at = Date.now(); } };
export const feedCacheKey = (mode, profileUserId) => `${mode}|${profileUserId || ""}`;

export function useReelsFeed({ token, mode, profileUserId, initialReelId, seriesId }) {
  const cacheKey = feedCacheKey(mode, profileUserId || seriesId);
  const cached = !initialReelId && FEED_CACHE.get(cacheKey);
  const fresh = cached && Date.now() - cached.at < CACHE_TTL && cached.reels.length > 0;
  const [reels, setReels] = useState(fresh ? cached.reels : []);
  const [loading, setLoading] = useState(!fresh);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(fresh ? cached.done : false);
  const seenRef = useRef(fresh ? new Set(cached.seen) : new Set());
  const seedRef = useRef(fresh ? cached.seed : Math.floor(Math.random() * 1e9));
  const restoredIndex = fresh ? cached.index : 0;

  useEffect(() => {
    if (loading) return;
    FEED_CACHE.set(cacheKey, { reels, seen: [...seenRef.current], seed: seedRef.current, done, index: FEED_CACHE.get(cacheKey)?.index || 0, at: Date.now() });
  }, [reels, done, loading, cacheKey]);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchPage = useCallback(async (reset = false) => {
    const exclude = reset ? [] : [...seenRef.current];
    const url = profileUserId
      ? `${API}/posts?user_id=${profileUserId}&media_only=true&limit=60`
      : seriesId
        ? `${API}/series/${seriesId}/play`
      : mode === "long"
        ? `${API}/posts/recommended-videos?kind=video&long=1&limit=${PAGE}&mode=for_you&seed=${seedRef.current}&exclude=${exclude.join(",")}`
        : `${API}/posts/recommended-videos?kind=all&limit=${PAGE}&mode=${mode}&seed=${seedRef.current}&exclude=${exclude.join(",")}`;
    const res = await axios.get(url, { headers }).catch(() => ({ data: [] }));
    let list = (Array.isArray(res.data) ? res.data : [])
      .map(normalizeReel)
      .filter((r) => r.video_url || r.image_url || (r.content || "").trim() || r.card_id);
    if (profileUserId) list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    list = list.filter((r) => reset || !seenRef.current.has(r.id));
    list.forEach((r) => seenRef.current.add(r.id));
    return list;
  }, [token, mode, profileUserId, seriesId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clave de la última carga: si el feed vino de caché y la clave no cambió, no refetch (seguro ante StrictMode).
  const depsKey = `${cacheKey}|${initialReelId || ""}`;
  const fetchedKey = useRef(fresh ? depsKey : null);
  useEffect(() => {
    if (fetchedKey.current === depsKey) return;
    fetchedKey.current = depsKey;
    let cancelled = false, completed = false;
    seenRef.current = new Set();
    seedRef.current = Math.floor(Math.random() * 1e9);
    setLoading(true); setDone(false);
    (async () => {
      const first = await fetchPage(true);
      if (cancelled) return;
      // Deep-link: make sure the requested reel is first
      if (initialReelId && !first.some((r) => r.id === initialReelId)) {
        const one = await axios.get(`${API}/posts/${initialReelId}`, { headers }).then((r) => normalizeReel(r.data)).catch(() => null);
        if (one) { first.unshift(one); seenRef.current.add(one.id); }
      } else if (initialReelId && !seriesId) {
        const idx = first.findIndex((r) => r.id === initialReelId);
        if (idx > 0) first.unshift(...first.splice(idx, 1));
      }
      setReels(first);
      setDone(profileUserId || seriesId ? true : first.length < PAGE);
      setLoading(false);
      completed = true;
    })();
    return () => { cancelled = true; if (!completed) fetchedKey.current = null; };
  }, [fetchPage, initialReelId, profileUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadingRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadingRef.current || loadingMore || done || profileUserId || seriesId) return;
    loadingRef.current = true;
    setLoadingMore(true);
    const more = await fetchPage(false);
    setReels((prev) => { const ids = new Set(prev.map((r) => r.id)); return [...prev, ...more.filter((r) => !ids.has(r.id))]; });
    if (more.length === 0) setDone(true);
    setLoadingMore(false);
    loadingRef.current = false;
  }, [fetchPage, loadingMore, done, profileUserId]);

  const trackView = useCallback((id) => {
    axios.post(`${API}/posts/${id}/view`, {}, { headers }).catch(() => {});
    try {
      const h = JSON.parse(localStorage.getItem("bruno-reels-history") || "[]").filter((x) => x !== id);
      h.unshift(id);
      localStorage.setItem("bruno-reels-history", JSON.stringify(h.slice(0, 200)));
    } catch (_) { /* ignore */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchReel = useCallback((id, patch) => {
    setReels((prev) => prev.map((r) => (r.id === id ? { ...r, ...(typeof patch === "function" ? patch(r) : patch) } : r)));
  }, []);

  const removeReel = useCallback((id) => setReels((prev) => prev.filter((r) => r.id !== id)), []);
  const removeByAuthor = useCallback((uid) => setReels((prev) => prev.filter((r) => (r.author?.id || r.user_id) !== uid)), []);
  return { reels, loading, loadingMore, done, loadMore, trackView, patchReel, removeReel, removeByAuthor, restoredIndex, cacheKey };
}
